import {FC, useState} from 'react'
import {Navigate} from 'react-router-dom'
import {Boxes, Gauge, KeyRound, ShieldCheck, UsersRound} from 'lucide-react'
import {PageTitle} from './../../../_metronic/layout/core'
import {useUsermenuData} from '../../hooks/useUserMenuData'
import {isAdmin} from '../../modules/auth'
import {AccessMatrixFiltersProvider} from '../../components/AccessMatrixFilters'

import UserList from './UserList'
import UsersPermissionMatrix from './UsersPermissionMatrix'
import {FeatureMatrix} from './FeatureMatrix'
import {ServiceLevelAccess} from './ServiceLevelAccess'
import './users-admin.css'
import './users-admin-extra.css'

type TabId = 'users' | 'menu-permissions' | 'features' | 'service-level'

const tabs: Array<{
  id: TabId
  label: string
  description: string
  icon: typeof UsersRound
}> = [
  {id: 'users', label: 'Usuários', description: 'Cadastros e status', icon: UsersRound},
  {
    id: 'menu-permissions',
    label: 'Permissões',
    description: 'Produtos e menus',
    icon: KeyRound,
  },
  {id: 'features', label: 'Funcionalidades', description: 'Recursos por pessoa', icon: Boxes},
  {
    id: 'service-level',
    label: 'Nível de Serviço',
    description: 'Acesso e escopo',
    icon: Gauge,
  },
]

const Users: FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('users')
  const {isFetching, isLojistasOnly} = useUsermenuData()

  if (isFetching) return null
  if (isLojistasOnly) return <Navigate to='/lojistas/geral' replace />
  if (!isAdmin()) return <Navigate to='/home' replace />

  return (
    <div className='user-admin'>
      <PageTitle>Administração de usuários</PageTitle>

      <section className='user-admin__hero'>
        <div className='user-admin__hero-icon' aria-hidden='true'>
          <ShieldCheck size={24} />
        </div>
        <div>
          <span className='user-admin__eyebrow'>Acesso e segurança</span>
          <h1>Administração de usuários</h1>
          <p>Gerencie pessoas, permissões e limites de acesso em um só lugar.</p>
        </div>
      </section>

      <nav className='user-admin__tabs' aria-label='Seções da administração de usuários'>
        {tabs.map((tab) => {
          const Icon = tab.icon
          const isActive = activeTab === tab.id

          return (
            <button
              key={tab.id}
              type='button'
              className={`user-admin__tab ${isActive ? 'is-active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className='user-admin__tab-icon'>
                <Icon size={19} />
              </span>
              <span>
                <strong>{tab.label}</strong>
                <small>{tab.description}</small>
              </span>
            </button>
          )
        })}
      </nav>

      <AccessMatrixFiltersProvider>
        <main className='user-admin__content'>
          {activeTab === 'users' && <UserList />}
          {activeTab === 'menu-permissions' && <UsersPermissionMatrix />}
          {activeTab === 'features' && <FeatureMatrix />}
          {activeTab === 'service-level' && <ServiceLevelAccess />}
        </main>
      </AccessMatrixFiltersProvider>
    </div>
  )
}

export default Users
